import { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import dayjs from 'dayjs';
import { rpx } from 'client/lib/rpx-client';
import { useTryAsyncData } from 'client/lib/hooks';
import { showError } from '@components/app-error';
import { BtnSecondary } from '@components/buttons';
import { DefaultSpinner } from '@components/spinner';
import { IcoCheck } from '@components/icons';
import { URLS } from 'shared/urls';
import { pluralize } from 'shared/formatting';
import { AccountKind } from 'shared/account-classification';
import 'shared/dateutil';

type Props = {
  courseId: UUID;
};

/**
 * The gate runs on a fixed Pacific clock, so these instants are shown in the
 * gate's timezone with an explicit PT label rather than converted to the
 * viewer's timezone.
 */
function fmtGateTime(dt: Date, timezone: string) {
  return `${dayjs(dt).tz(timezone).format('ddd, MMM D [at] h:mm A')} PT`;
}

function fmtGateWeekday(dt: Date, timezone: string) {
  return dayjs(dt).tz(timezone).format('ddd');
}

function HeadCell({
  children,
  class: className = '',
}: {
  class?: string;
  children: ComponentChildren;
}) {
  return <div class={`table-cell p-4 py-2 align-bottom ${className}`}>{children}</div>;
}

function BodyCell({
  children,
  class: className = '',
}: {
  class?: string;
  children: ComponentChildren;
}) {
  return <span class={`table-cell p-4 border-t align-top ${className}`}>{children}</span>;
}

/**
 * Staff and test accounts sit on cohort rosters on purpose. The gate never
 * emails or removes them, but they stay on the report wearing a pill — a real
 * customer whose address happens to match a test pattern has to be visible,
 * not silently exempt.
 */
function AccountPill({ kind }: { kind: Exclude<AccountKind, 'member'> }) {
  const label = kind === 'staff' ? 'Staff' : 'Test';
  const color = kind === 'staff' ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-800';
  return (
    <span class={`rounded-sm px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide ${color}`}>
      {label}
    </span>
  );
}

function SettingsLink({ courseId }: Props) {
  return (
    <a
      class="text-indigo-600 underline"
      href={URLS.guide.coursePage({ courseId, page: 'settings/completion-gate' })}
    >
      completion gate settings
    </a>
  );
}

/**
 * An excused-through date is a plain gate-local date, so it is formatted as
 * written — parsing it into the viewer's timezone would shift it a day for
 * anyone east of Pacific.
 */
function fmtExcusedThrough(date: string) {
  return dayjs(date).format('ddd, MMM D');
}

export function CertGateTab({ courseId }: Props) {
  // Bumped after a restore so the report refetches. Without it the student
  // vanishes from both tables — they leave the removed list, and the behind
  // list was built while they were still disabled — so the Excuse button the
  // copy points them at has no row to sit on until a page reload.
  const [reloadCount, setReloadCount] = useState(0);
  const loader = useTryAsyncData(
    () => rpx.manageStudents.getCertGateReport({ courseId }),
    [courseId, reloadCount],
  );
  const report = loader.data;
  // Local overrides for the excuse toggle: a key is present once this guide
  // has changed that student, and its value is the server's answer.
  const [excuseOverrides, setExcuseOverrides] = useState<Record<UUID, { excusedThrough?: string }>>(
    {},
  );
  const [savingUserId, setSavingUserId] = useState<UUID | undefined>(undefined);
  // Excuse overrides are answers about one course's students; they would
  // otherwise survive a switch to another course's tab.
  useEffect(() => {
    setExcuseOverrides({});
  }, [courseId]);

  async function restore(userId: UUID) {
    setSavingUserId(userId);
    try {
      await rpx.manageStudents.restoreCertGateRemoval({ courseId, userId });
      setReloadCount((n) => n + 1);
    } catch (err) {
      showError(err);
    } finally {
      setSavingUserId(undefined);
    }
  }

  async function toggleExcused(userId: UUID, excused: boolean) {
    setSavingUserId(userId);
    try {
      const result = await rpx.manageStudents.setCertGateExemption({ courseId, userId, excused });
      setExcuseOverrides((overrides) => ({
        ...overrides,
        [userId]: { excusedThrough: result.excusedThrough },
      }));
    } catch (err) {
      showError(err);
    } finally {
      setSavingUserId(undefined);
    }
  }

  if (!report) {
    if (loader.isLoading) {
      return <DefaultSpinner />;
    }
    return (
      <div class="bg-white rounded-sm border p-6 text-sm text-gray-600">
        We couldn't load the completion gate report. Refresh the page to try again.
      </div>
    );
  }

  const removed = report.removed;
  // Built before the "gate is off" branch below: turning the gate off after
  // a cohort ends is a normal thing to do, and it must not strand the
  // students it removed with no way back.
  const removedSection = removed.length > 0 && (
    <div class="flex flex-col gap-3">
      <span class="flex flex-col">
        <strong class="text-lg text-gray-800">
          {removed.length} {pluralize('student', removed.length)} removed by the gate
        </strong>
        <span class="text-sm text-gray-600">
          Still out of the course. Restoring puts a student back in for the rest of this week.
          Wednesday's check counts everything outstanding, so anyone still behind is removed again.
        </span>
      </span>
      <div class="table table-fixed bg-white rounded-sm border w-full divide-y">
        <div class="table-row bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          <HeadCell class="w-1/3">Student</HeadCell>
          <HeadCell>Still missing</HeadCell>
          <HeadCell class="w-36">Removed</HeadCell>
          {report.canRestore && <HeadCell class="w-48">Access</HeadCell>}
        </div>
        {removed.map((s) => (
          <div key={s.userId} class="table-row text-sm">
            <BodyCell>
              <span class="flex flex-col">
                <span class="flex items-center gap-2">
                  <strong class="text-gray-800">{s.name}</strong>
                  {s.accountKind !== 'member' && <AccountPill kind={s.accountKind} />}
                </span>
                <span class="text-gray-500 truncate">{s.email}</span>
              </span>
            </BodyCell>
            <BodyCell>
              {s.missingLessons.length === 0 ? (
                <span class="text-gray-500">Nothing outstanding now</span>
              ) : (
                <span class="text-gray-800">
                  {s.missingLessons.length} {pluralize('lesson', s.missingLessons.length)} missing
                </span>
              )}
            </BodyCell>
            <BodyCell>
              <span class="text-gray-600">{fmtGateTime(s.removedAt, report.timezone)}</span>
            </BodyCell>
            {report.canRestore && (
              <BodyCell>
                <BtnSecondary
                  class="text-xs"
                  isLoading={savingUserId === s.userId}
                  onClick={() => restore(s.userId)}
                >
                  Restore access
                </BtnSecondary>
              </BodyCell>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  if (!report.enabled) {
    return (
      <div class="flex flex-col gap-6">
        <div class="bg-white rounded-sm border p-6 flex flex-col gap-2">
          <strong class="text-gray-800">The completion gate is off for this course.</strong>
          <span class="text-sm text-gray-600">
            Turn it on and pick at least one gated module in <SettingsLink courseId={courseId} />.
            Once it's on, this tab lists every student with missing lessons, the reminders they were
            sent, and what Wednesday's removal check will do.
          </span>
        </div>
        {removedSection}
      </div>
    );
  }

  // Only students the crons will actually act on drive the headline number —
  // staff and test accounts are listed below it, never counted in it.
  const enforceable = report.students.filter((s) => s.accountKind === 'member');
  const skipped = report.students.filter((s) => s.accountKind !== 'member');
  // Behind first — they are what the screen is for. Everyone else is listed
  // underneath so a guide can excuse someone who is still caught up but has
  // already told them they will not finish this week.
  const isBehind = (s: (typeof enforceable)[number]) => s.missingLessons.length > 0;
  const listed = [
    ...enforceable.filter(isBehind),
    ...skipped.filter(isBehind),
    ...enforceable.filter((s) => !isBehind(s)),
    ...skipped.filter((s) => !isBehind(s)),
  ];
  const numBehind = enforceable.filter(isBehind).length;
  // A finished gate removes nobody, so the Monday-removal column would only
  // promise an action that will not happen.
  const showRemovalColumn = report.autoRemoval && !report.enforcementFinished;

  return (
    <div class="flex flex-col gap-6">
      <header class="bg-white rounded-sm border flex flex-col gap-4 p-6">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <span class="flex flex-col">
            <strong class="text-2xl text-gray-800">
              {numBehind} of {report.totalStudents} {pluralize('student', report.totalStudents)}{' '}
              behind
            </strong>
            <span class="text-sm text-gray-600">
              Counts gated-module lessons released as of Sunday 8:00 AM PT — the same set the
              reminder emails named and Wednesday's check enforces.
            </span>
          </span>
          <span class="flex flex-col text-sm text-gray-600 md:text-right">
            {report.enforcementFinished ? (
              <span>
                Finished after <b>{fmtExcusedThrough(report.lastEnforcedOn || '')}</b>
              </span>
            ) : (
              <>
                {report.nextCheckScheduled && (
                  <>
                    {report.nextEmailScheduled && (
                      <span>
                        Next reminder email{' '}
                        <b>{fmtGateTime(report.nextEmailAt, report.timezone)}</b>
                      </span>
                    )}
                    <span>
                      Next removal check{' '}
                      <b>{fmtGateTime(report.nextEnforcementAt, report.timezone)}</b>
                    </span>
                  </>
                )}
                {report.lastEnforcedOn && (
                  <span>
                    {report.nextCheckScheduled ? 'Last removal day' : 'Final week — gate ends'}{' '}
                    <b>{fmtExcusedThrough(report.lastEnforcedOn)}</b>
                  </span>
                )}
              </>
            )}
          </span>
        </div>
        {!report.coursePublished && (
          <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            <b>This course is not published.</b> The reminder emails and Wednesday removal check
            only run on published courses — nothing below will happen until the course is published.
          </div>
        )}
        {report.enforcementFinished ? (
          <div class="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            <b>This gate has finished.</b> It stopped after{' '}
            {fmtExcusedThrough(report.lastEnforcedOn || '')}. Nobody is emailed on Sundays or
            Mondays{report.autoRemoval ? ' or removed on Wednesdays' : ''} any more. Change the last
            removal day in <SettingsLink courseId={courseId} /> to start it again.
          </div>
        ) : report.autoRemoval ? (
          <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <b>Auto-removal ON</b> — warned students missing {report.minDaysMissing}{' '}
            {pluralize('day', report.minDaysMissing)} or more at Wednesday 8:00 AM PT will be
            removed. Students missing fewer are kept ("Nearly there"). Students with no reminder
            email on record are skipped until the next cycle. Excuse anyone below to let them keep
            their access through one removal check; the following Wednesday counts everything still
            outstanding, that week included.
          </div>
        ) : (
          <div class="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            <b>Auto-removal off</b> — report only. No one is removed automatically.
          </div>
        )}
      </header>

      {listed.length === 0 && (
        <div class="bg-white rounded-sm border p-6 text-sm text-gray-600">
          No students are enrolled in this course yet.
        </div>
      )}

      {listed.length > 0 && (
        <div class="table table-fixed bg-white rounded-sm border w-full divide-y">
          <div class="table-row bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <HeadCell class="w-1/3">Student</HeadCell>
            <HeadCell>Missing</HeadCell>
            <HeadCell class="w-36">Reminder</HeadCell>
            {showRemovalColumn && <HeadCell class="w-48">Wednesday removal</HeadCell>}
          </div>
          {listed.map((s) => {
            const modules: { moduleId: UUID; moduleTitle: string; lessons: string[] }[] = [];
            for (const lesson of s.missingLessons) {
              const current = modules[modules.length - 1];
              if (current?.moduleId === lesson.moduleId) {
                current.lessons.push(lesson.lessonTitle);
              } else {
                modules.push({
                  moduleId: lesson.moduleId,
                  moduleTitle: lesson.moduleTitle,
                  lessons: [lesson.lessonTitle],
                });
              }
            }
            const override = excuseOverrides[s.userId];
            const excusedThrough = override ? override.excusedThrough : s.excusedThrough;
            const isEnforceable = s.accountKind === 'member';
            const behind = s.missingLessons.length > 0;
            const nearlyThere = behind && s.daysMissing < report.minDaysMissing;
            return (
              <div key={s.userId} class="table-row text-sm">
                <BodyCell>
                  <span class="flex flex-col">
                    <span class="flex items-center gap-2">
                      <strong class="text-gray-800">{s.name}</strong>
                      {s.accountKind !== 'member' && <AccountPill kind={s.accountKind} />}
                    </span>
                    <span class="text-gray-500 truncate">{s.email}</span>
                  </span>
                </BodyCell>
                <BodyCell>
                  {!behind && <span class="text-gray-500">Caught up</span>}
                  {behind && (
                    <span class="flex flex-col gap-2">
                      {modules.map((m) => (
                        <span key={m.moduleId} class="flex flex-col">
                          <span class="text-xs uppercase tracking-wider text-gray-500">
                            {m.moduleTitle}
                          </span>
                          {m.lessons.map((title, i) => (
                            <span key={i} class="text-gray-800">
                              {title}
                            </span>
                          ))}
                        </span>
                      ))}
                      <span class="flex items-center gap-2 text-xs text-gray-500">
                        {s.missingLessons.length} {pluralize('lesson', s.missingLessons.length)}{' '}
                        missing · {s.daysMissing} {pluralize('day', s.daysMissing)}
                        {nearlyThere && (
                          <span class="rounded-full bg-sky-50 px-2 py-0.5 text-sky-800">
                            Nearly there
                          </span>
                        )}
                      </span>
                    </span>
                  )}
                </BodyCell>
                <BodyCell>
                  {!isEnforceable ? (
                    <span class="text-gray-400">Not emailed</span>
                  ) : s.warnedAt ? (
                    <span class="flex items-center gap-1 text-green-700">
                      <IcoCheck class="w-4 h-4" />
                      Warned {fmtGateWeekday(s.warnedAt, report.timezone)}
                    </span>
                  ) : behind ? (
                    <span class="text-gray-400">Not yet sent</span>
                  ) : (
                    <span class="text-gray-400">Nothing to send</span>
                  )}
                </BodyCell>
                {showRemovalColumn && (
                  <BodyCell>
                    <span class="flex flex-col items-start gap-1">
                      {!isEnforceable ? (
                        <span class="text-gray-500">Not counted</span>
                      ) : excusedThrough ? (
                        <>
                          <span class="text-gray-800">
                            Excused through {fmtExcusedThrough(excusedThrough)}
                          </span>
                          <BtnSecondary
                            class="text-xs"
                            isLoading={savingUserId === s.userId}
                            onClick={() => toggleExcused(s.userId, false)}
                          >
                            Undo
                          </BtnSecondary>
                        </>
                      ) : (
                        <>
                          <span class="text-gray-500">
                            {!behind ? 'Not at risk' : nearlyThere ? 'Kept' : 'Will be removed'}
                          </span>
                          <BtnSecondary
                            class="text-xs"
                            isLoading={savingUserId === s.userId}
                            onClick={() => toggleExcused(s.userId, true)}
                          >
                            Excuse this week
                          </BtnSecondary>
                        </>
                      )}
                    </span>
                  </BodyCell>
                )}
              </div>
            );
          })}
        </div>
      )}

      {skipped.length > 0 && (
        <span class="text-sm text-gray-500 -mt-3">
          {skipped.length} staff or test {pluralize('account', skipped.length)} on this roster
          {skipped.length === 1 ? ' is' : ' are'} listed but never emailed or removed.
        </span>
      )}

      {removedSection}
    </div>
  );
}
